import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fadeUp, viewportOnce } from "@/lib/motion";
import { getTracking } from "@/lib/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAITLIST_BG =
  "https://images.unsplash.com/photo-1656420731611-ffae337fab6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb3RvcmN5Y2xlJTIwcmVwYWlyJTIwbWVjaGFuaWMlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg0NDMwMTg5fDA&ixlib=rb-4.1.0&q=85";

const inputClass =
  "bg-[#1A1D24] border-white/10 text-[#F4F1ED] placeholder:text-[#F4F1ED]/35 h-12 rounded-xl focus-visible:ring-2 focus-visible:ring-[#E7B459] focus-visible:border-[#E7B459] focus-visible:ring-offset-0";

const emptyForm = {
  full_name: "",
  mobile: "",
  city: "",
  brand: "",
  model: "",
  frustration: "",
};

export default function WaitlistSection() {
  const [form, setForm] = useState(emptyForm);
  const [interview, setInterview] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    const mobile = form.mobile.replace(/\D/g, "").replace(/^91/, "").replace(/^0/, "");
    if (!/^[6-9]\d{9}$/.test(mobile))
      e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!form.city.trim()) e.city = "Please enter your city.";
    if (!form.brand.trim()) e.brand = "Please enter your two-wheeler brand.";
    if (!form.model.trim()) e.model = "Please enter your two-wheeler model.";
    if (!consent) e.consent = "Please accept the consent to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        ...form,
        consent,
        open_to_interview: interview,
        tracking: getTracking(),
      });
      setStatus("success");
      if (data.duplicate) {
        toast("Looks like you're already on the list — updated your details!");
      }
    } catch (err) {
      setStatus("idle");
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-28 md:py-36 px-6 md:px-12 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <img src={WAITLIST_BG} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-[#1A1D24]/92" />
      </div>

      <div className="max-w-xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mb-10"
        >
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#E7B459] mb-6">
            04 — Early access
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Help us build Raahi and ride the wave of next-gen aftermarket.
          </h2>
          <p className="mt-5 text-lg text-[#F4F1ED]/70 leading-relaxed">
            Raahi is a company still under development — and you can be part of
            the journey by joining our waitlist. Help us shape a simpler, faster
            way to maintain your two-wheeler. We may also reach out to a few
            riders for a short product interview.
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {status === "success" ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="waitlist-success"
              className="rounded-3xl bg-[#2D323D] border border-[#E7B459]/30 p-10 text-center"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#E7B459]/15">
                <CheckCircle2 className="h-9 w-9 text-[#E7B459]" />
              </div>
              <h3 className="font-display text-2xl font-semibold mb-3">
                You're on the list.
              </h3>
              <p className="text-[#F4F1ED]/70 leading-relaxed">
                Thank you for helping us build a better ownership experience for
                Indian riders.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              data-testid="waitlist-form"
              noValidate
              className="rounded-3xl bg-[#2D323D]/70 backdrop-blur-md border border-white/10 p-6 md:p-8 space-y-5"
            >
              <Field label="Full name" id="full_name" error={errors.full_name}>
                <Input
                  id="full_name"
                  data-testid="input-full-name"
                  className={inputClass}
                  placeholder="e.g. Arjun Sharma"
                  value={form.full_name}
                  onChange={setField("full_name")}
                />
              </Field>

              <Field label="Mobile number" id="mobile" error={errors.mobile}>
                <Input
                  id="mobile"
                  data-testid="input-mobile"
                  type="tel"
                  inputMode="numeric"
                  className={inputClass}
                  placeholder="10-digit mobile number"
                  value={form.mobile}
                  onChange={setField("mobile")}
                />
              </Field>

              <Field label="City" id="city" error={errors.city}>
                <Input
                  id="city"
                  data-testid="input-city"
                  className={inputClass}
                  placeholder="e.g. Pune"
                  value={form.city}
                  onChange={setField("city")}
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Two-wheeler brand" id="brand" error={errors.brand}>
                  <Input
                    id="brand"
                    data-testid="input-brand"
                    className={inputClass}
                    placeholder="e.g. Honda"
                    value={form.brand}
                    onChange={setField("brand")}
                  />
                </Field>
                <Field label="Two-wheeler model" id="model" error={errors.model}>
                  <Input
                    id="model"
                    data-testid="input-model"
                    className={inputClass}
                    placeholder="e.g. Activa 6G"
                    value={form.model}
                    onChange={setField("model")}
                  />
                </Field>
              </div>

              <Field
                label="What frustrates you most about maintaining your bike or scooter?"
                id="frustration"
                optional
              >
                <Textarea
                  id="frustration"
                  data-testid="input-frustration"
                  className={`${inputClass} h-24 py-3`}
                  placeholder="Optional — tell us what bothers you the most."
                  value={form.frustration}
                  onChange={setField("frustration")}
                />
              </Field>

              <label className="flex items-start gap-3 cursor-pointer pt-1">
                <Checkbox
                  checked={interview}
                  onCheckedChange={(v) => setInterview(Boolean(v))}
                  data-testid="checkbox-interview"
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#E7B459] data-[state=checked]:border-[#E7B459] data-[state=checked]:text-[#1A1D24]"
                />
                <span className="text-sm text-[#F4F1ED]/70 leading-relaxed">
                  I'm open to a short product interview to help shape Raahi.
                </span>
              </label>

              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={consent}
                    onCheckedChange={(v) => {
                      setConsent(Boolean(v));
                      setErrors((p) => ({ ...p, consent: undefined }));
                    }}
                    data-testid="checkbox-consent"
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-[#D9544E] data-[state=checked]:border-[#D9544E] data-[state=checked]:text-[#F4F1ED]"
                  />
                  <span className="text-sm text-[#F4F1ED]/70 leading-relaxed">
                    I agree to be contacted by Raahi regarding early access,
                    product research and updates.
                  </span>
                </label>
                {errors.consent && (
                  <p data-testid="error-consent" className="mt-2 text-sm text-[#D9544E]">
                    {errors.consent}
                  </p>
                )}
              </div>

              <button
                type="submit"
                data-testid="waitlist-submit-btn"
                disabled={status === "submitting"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D9544E] text-[#F4F1ED] text-base font-semibold px-8 py-4 hover:bg-[#c4423d] transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Joining…
                  </>
                ) : (
                  <>
                    Join the Raahi Waitlist
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Field({ label, id, error, optional, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-[#F4F1ED]/85">
        {label}
        {optional && <span className="text-[#F4F1ED]/40"> (optional)</span>}
      </Label>
      {children}
      {error && (
        <p data-testid={`error-${id}`} className="text-sm text-[#D9544E]">
          {error}
        </p>
      )}
    </div>
  );
}
